// Comment Parser Utility
import { TranslationFile, AuditDetails } from '../types';
import { getLineSegments } from './textUtils';
// Parses audit comments to extract structured tags (Lỗi, Rule, Tham khảo, Cần xem lại)

export interface ParsedCommentItem {
    type: 'loi' | 'rule' | 'thamkhao' | 'canxemlai' | 'ok' | 'other';
    content: string;
    source?: string;  // For Rule: 'A' -> 'B' or 'A' -> 'B' -> 'C'
    aiTerm?: string;  // For Rule: Middle term if 3 parts
    target?: string;
}

export interface ParsedCommentWithMeta extends ParsedCommentItem {
    fileId: string;
    fileName: string;
    segmentIndex: number;
    score?: number;
    sourceText?: string;  // Original source text
    aiText?: string;      // AI translated text
    humanText?: string;   // Human benchmark text
}

/**
 * Parse a single comment string into structured items
 */
export const parseComment = (comment: string): ParsedCommentItem[] => {
    if (!comment || comment.trim() === '-' || comment.trim().toLowerCase() === 'ok') {
        return [{ type: 'ok', content: comment || 'OK' }];
    }

    const items: ParsedCommentItem[] = [];

    // Normalize the comment: split by various delimiters
    const normalized = comment
        .replace(/([.!?])\s+(Rule:|Lỗi:|Cần xem lại:)/g, '$1\n$2')
        .replace(/,\s*-\s*/g, '\n- ')
        .replace(/\s+-\s+/g, '\n- ')
        .replace(/\s+(Rule:|Lỗi:|Cần xem lại:)/g, '\n$1');

    const parts = normalized
        .split(/\n/)
        .map(s => s.trim())
        .filter(Boolean);

    for (const part of parts) {
        const cleanPart = part.replace(/^-\s*/, '').trim();

        // Check for Rule: 'A' -> 'B' -> 'C' OR 'A' -> 'B'
        const ruleMatch = cleanPart.match(/^Rules?:\s*['\"]?(.+?)['\"]?\s*(?:->|>|→)\s*['\"]?(.+?)['\"]?(?:\s*(?:->|>|→)\s*['\"]?(.+?)['\"]?)?$/i);
        if (ruleMatch) {
            items.push({
                type: 'rule',
                content: cleanPart,
                source: ruleMatch[1].trim().replace(/['"]/g, ''),
                aiTerm: ruleMatch[3] ? ruleMatch[2].trim().replace(/['"]/g, '') : undefined,
                target: (ruleMatch[3] || ruleMatch[2]).trim().replace(/['"]/g, '')
            });
            continue;
        }

        // Check for Lỗi: pattern
        if (/^Lỗi:/i.test(cleanPart)) {
            items.push({
                type: 'loi',
                content: cleanPart.replace(/^Lỗi:\s*/i, '').trim()
            });
            continue;
        }


        // Check for Cần xem lại: pattern
        if (/^Cần xem lại:/i.test(cleanPart)) {
            items.push({
                type: 'canxemlai',
                content: cleanPart.replace(/^Cần xem lại:\s*/i, '').trim()
            });
            continue;
        }

        // If none matched and not empty, classify as other
        if (cleanPart && cleanPart.toLowerCase() !== 'ok') {
            items.push({
                type: 'other',
                content: cleanPart
            });
        }
    }

    // If nothing was parsed, return as OK
    if (items.length === 0) {
        return [{ type: 'ok', content: comment }];
    }

    return items;
};

/**
 * Aggregate parsed comments from all files in a project
 */
export const aggregateProjectComments = (
    files: Array<{
        id: string;
        fileName: string;
        sourceText?: string;
        backendSegments?: string[]; // DOCX raw paragraph segments (CÓ THỂ NHIỀU HƠN aiOutput vì filter)
        aiOutput?: string[];  // Filtered/translated segments — index gốc cho audit.sourceIndex
        humanBenchmark?: string;
        // Semantic match result — chứa sourceText/humanText đã align cùng index với aiOutput
        semanticMatchResult?: {
            mappings?: Array<{
                sourceIndex: number; // 0-based — match audit.sourceIndex - 1
                sourceText?: string;
                humanText?: string;
                allHumanIndices?: number[];
                isUnmatched?: boolean;
            }>;
        };
        auditResult?: {
            segmentMappings?: Array<{
                sourceIndex: number;
                humanIndices?: number[];
                score: number;
                rules?: string;
                errors?: string;
                reviews?: string;
                comment?: string; // Fallback
            }>;
        };
    }>,
    projectHumanBenchmark?: string  // Fallback from project level
): {
    loi: ParsedCommentWithMeta[];
    rule: ParsedCommentWithMeta[];
    canxemlai: ParsedCommentWithMeta[];
} => {
    const result = {
        loi: [] as ParsedCommentWithMeta[],
        rule: [] as ParsedCommentWithMeta[],
        canxemlai: [] as ParsedCommentWithMeta[]
    };

    for (const file of files) {
        const mappings = file.auditResult?.segmentMappings || [];

        // CRITICAL alignment:
        //   audit.sourceIndex (1-based) → index vào FILTERED list aiOutput[]
        //   KHÔNG phải vào backendSegments[] (raw, có thể nhiều hơn vì lọc số/phone/formula)
        // Source text đúng cùng index = semanticMatchResult.mappings[i].sourceText (cùng 189 entries với aiOutput)
        // Fallback chain: semanticMatch → backendSegments → getLineSegments(sourceText)
        const smMappings = file.semanticMatchResult?.mappings || [];
        const aiLines = file.aiOutput || [];

        // Build sourceLines aligned với aiLines:
        // Ưu tiên semanticMatch (đảm bảo cùng filter); fallback backendSegments hoặc legacy split.
        let sourceLines: string[];
        if (smMappings.length > 0 && smMappings.length === aiLines.length) {
            // Best case: semantic match đã align 1:1 với aiOutput
            sourceLines = smMappings.map(m => m.sourceText || '');
        } else if (file.backendSegments && file.backendSegments.length === aiLines.length) {
            // Hiếm: backendSegments khớp số lượng aiOutput (TXT files không lọc)
            sourceLines = file.backendSegments;
        } else {
            // Legacy: split sourceText — chỉ đúng nếu file không filter (TXT/MD)
            sourceLines = getLineSegments(file.sourceText || '').map(s => s.text);
        }

        // Use file humanBenchmark first, fallback to project level
        const humanBenchmarkText = file.humanBenchmark || projectHumanBenchmark || '';
        const humanLines = getLineSegments(humanBenchmarkText).map(s => s.text);

        for (const mapping of mappings) {
            const humanIndices = mapping.humanIndices || [];
            // CSV sourceIndex is 1-based, convert to 0-based for array access
            const zeroBasedIndex = mapping.sourceIndex - 1;
            const sourceText = sourceLines[zeroBasedIndex] || '';
            const aiText = aiLines[zeroBasedIndex] || '';

            // Aggregate from structured fields
            const itemsToProcess: Array<{ type: 'rule' | 'loi' | 'canxemlai' | 'other', content: string, source?: string, aiTerm?: string, target?: string }> = [];

            if (mapping.rules) {
                // Split by semicolon OR newline to handle better
                mapping.rules.split(/[;\n]+/).forEach(r => {
                    const content = r.trim();
                    if (!content) return;

                    // Parse 'A -> B -> C' OR 'A -> B' pattern
                    const arrowMatch = content.match(/^(.+?)\s*(?:->|→|>)\s*(.+?)(?:\s*(?:->|→|>)\s*(.+))?$/);
                    if (arrowMatch) {
                        itemsToProcess.push({
                            type: 'rule',
                            content,
                            source: arrowMatch[1].trim(),
                            aiTerm: arrowMatch[3] ? arrowMatch[2].trim() : undefined,
                            target: (arrowMatch[3] || arrowMatch[2]).trim()
                        });
                    } else {
                        // Fallback: use content as both source and target
                        itemsToProcess.push({ type: 'rule', content, source: content, target: '' });
                    }
                });
            }
            if (mapping.errors) {
                mapping.errors.split(/[;\n]+/).forEach(e => {
                    const clean = e.trim();
                    if (clean) itemsToProcess.push({ type: 'loi', content: clean });
                });
            }
            if (mapping.reviews) {
                mapping.reviews.split(/[;\n]+/).forEach(r => {
                    const clean = r.trim();
                    if (clean) itemsToProcess.push({ type: 'canxemlai', content: clean });
                });
            }

            // Fallback to old comment field if no structured data
            if (itemsToProcess.length === 0 && mapping.comment && mapping.comment !== 'OK' && mapping.comment !== '-') {
                const parsed = parseComment(mapping.comment);
                parsed.forEach(p => {
                    if (p.type === 'rule' || p.type === 'loi' || p.type === 'canxemlai') {
                        itemsToProcess.push({ type: p.type, content: p.content });
                    }
                });
            }

            // Trust the sequential mapping from getLineSegments which aligns with UI
            // If humanIndices exists, use them (mapped to smart segments indices)
            let humanText = '';

            // Priority:
            // 0. SemanticMatch already pre-aligned humanText với aiOutput → ưu tiên dùng nếu có
            // 1. If humanIndices provided by AI, try to correctly map them
            // 2. Fallback to 1:1 mapping (sequential)
            const smMatch = smMappings[zeroBasedIndex];
            if (smMatch && smMatch.humanText && !smMatch.isUnmatched) {
                humanText = smMatch.humanText;
            } else if (humanIndices.length > 0) {
                humanText = humanIndices
                    .map(i => humanLines[i - 1]) // 1-based to 0-based
                    .filter(Boolean)
                    .join(' ');

                // Fallback: If AI indices point to nothing (e.g. out of bounds due to splitting diff), try sequential
                if (!humanText && humanLines.length > zeroBasedIndex) {
                    humanText = humanLines[zeroBasedIndex];
                }
            } else if (humanLines.length > zeroBasedIndex) {
                humanText = humanLines[zeroBasedIndex] || '';
            }

            for (const item of itemsToProcess) {

                const withMeta: ParsedCommentWithMeta = {
                    ...item,
                    fileId: file.id,
                    fileName: file.fileName,
                    segmentIndex: mapping.sourceIndex,
                    score: mapping.score,
                    sourceText,
                    aiText,
                    humanText
                };

                if (item.type === 'loi') result.loi.push(withMeta);
                else if (item.type === 'rule') result.rule.push(withMeta);
                else if (item.type === 'canxemlai') result.canxemlai.push(withMeta);
            }
        }
    }

    return result;
};

/**
 * Deduplicate rules by source->target
 */
export const deduplicateRules = (
    rules: ParsedCommentWithMeta[]
): Array<ParsedCommentWithMeta & { count: number; files: string[]; occurrences: ParsedCommentWithMeta[] }> => {
    const map = new Map<string, ParsedCommentWithMeta & { count: number; files: string[]; occurrences: ParsedCommentWithMeta[] }>();

    for (const rule of rules) {
        const key = `${rule.source}::${rule.target}`;
        if (map.has(key)) {
            const existing = map.get(key)!;
            existing.count++;
            if (!existing.files.includes(rule.fileName)) {
                existing.files.push(rule.fileName);
            }
            existing.occurrences.push(rule);
        } else {
            map.set(key, { ...rule, count: 1, files: [rule.fileName], occurrences: [rule] });
        }
    }

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
};
